import { Badge, BlockStack, Button, Card, InlineStack, Text } from '@shopify/polaris'
import type { CategoryReport } from '../../../../sdk/health.api'

const CATEGORY_TITLE: Record<string, string> = {
  orders:    'Orders',
  products:  'Products / SKUs',
  inventory: 'Inventory',
  shipping:  'Shipping',
  finance:   'Finance',
}

const STATUS_BADGE: Record<string, { tone: 'success' | 'warning' | 'critical' | 'info'; label: string }> = {
  healthy:         { tone: 'success',  label: 'Healthy'           },
  needs_attention: { tone: 'warning',  label: 'Needs Attention'   },
  critical:        { tone: 'critical', label: 'Critical'          },
  not_configured:  { tone: 'info',     label: 'Not Configured'    },
}

const SEVERITY_COLOR: Record<string, string> = {
  critical: '#bf0711',
  warning:  '#916a00',
  info:     '#5c6ac4',
}

type Props = {
  cat:          CategoryReport
  onViewIssues: (validatorId: string) => void
}

export function CategoryHealthCard({ cat, onViewIssues }: Props) {
  const badge = STATUS_BADGE[cat.status] ?? STATUS_BADGE.healthy
  const activeValidators = cat.validators.filter(v => v.affectedCount > 0)

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <Text as="h3" variant="headingSm">{CATEGORY_TITLE[cat.category] ?? cat.category}</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {cat.totalRows > 0 ? `${cat.totalRows} orders` : 'No data'}
            </Text>
          </BlockStack>
          <BlockStack gap="100" inlineAlign="end">
            <div style={{ fontSize: 28, fontWeight: 700, color: badge.tone === 'success' ? '#108043' : badge.tone === 'critical' ? '#bf0711' : '#916a00' }}>
              {cat.healthPct}%
            </div>
            <Badge tone={badge.tone}>{badge.label}</Badge>
          </BlockStack>
        </InlineStack>

        {activeValidators.length > 0 && (
          <BlockStack gap="150">
            {activeValidators.map((v) => (
              <InlineStack key={v.validatorId} align="space-between" blockAlign="center">
                <InlineStack gap="100" blockAlign="center">
                  <span style={{ color: SEVERITY_COLOR[v.severity], fontSize: 10 }}>●</span>
                  <Text as="span" variant="bodySm">{v.title}</Text>
                </InlineStack>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="span" variant="bodySm" tone="subdued">{v.affectedCount} orders</Text>
                  <Button
                    variant="plain"
                    size="micro"
                    onClick={() => onViewIssues(v.validatorId)}
                  >
                    View
                  </Button>
                </InlineStack>
              </InlineStack>
            ))}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  )
}
